import { KeyRound, ShieldCheck, ShieldOff, UserPlus, X } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  freshSignInMinutes,
  type StaffMember,
  type StaffOverview,
  type StaffRole,
  type StaffRoleChangeResult,
  staffSignIn
} from "../../shared/staff";
import { api, ApiError, type Session } from "./api";
import { demoStaffOverview } from "./staff-demo";
import {
  Empty,
  fmtInt,
  fmtRelative,
  Notice,
  PageHead,
  SkeletonRows,
  StatusDot,
  useIsNarrow
} from "./ui";

type Available = Extract<StaffOverview, { available: true }>;
type Outcome = { tone: "success" | "error"; text: string };

/** A grant typed before "Confirm with sign-in", restored when the console returns here. */
const pendingKey = "evidite-admin-staff-pending";

function readPending(): { email?: string; role?: string } {
  try {
    return (JSON.parse(sessionStorage.getItem(pendingKey) ?? "{}") ?? {}) as {
      email?: string;
      role?: string;
    };
  } catch {
    return {};
  }
}

const fmtTime = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export function StaffView({ session, demo }: { session: Session; demo: boolean }) {
  const narrow = useIsNarrow();
  const [overview, setOverview] = useState<StaffOverview>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [outcome, setOutcome] = useState<Outcome>();
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!quiet) setLoading(true);
      setError(undefined);
      try {
        setOverview(demo ? demoStaffOverview() : await api<StaffOverview>("/api/staff"));
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : "Couldn't load the staff list. Try again shortly."
        );
      } finally {
        setLoading(false);
      }
    },
    [demo]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Re-check the fresh-sign-in window every half minute so the banner turns
  // over when it lapses.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const available = overview?.available ? overview : undefined;
  const fresh =
    available?.freshUntil && new Date(available.freshUntil).getTime() > now
      ? available.freshUntil
      : null;

  async function change(action: "grant" | "revoke", email: string, role: StaffRole) {
    setOutcome(undefined);
    try {
      const result = demo
        ? demoChange(action, email, role.role)
        : await api<StaffRoleChangeResult>(`/api/staff/${action}`, session, {
            method: "POST",
            body: JSON.stringify({ email, role: role.role })
          });
      const changed = action === "grant" ? result.assigned : result.revoked;
      setOutcome({
        tone: "success",
        text: changed
          ? `${action === "grant" ? "Granted" : "Revoked"} ${role.displayName} ${
              action === "grant" ? "to" : "from"
            } ${result.email}. It applies to their console within about 10 minutes, or at once when they sign in again.`
          : `${result.email} ${action === "grant" ? "already has" : "doesn't have"} ${role.displayName}; nothing changed.`
      });
      if (!demo) await load({ quiet: true });
      return true;
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "reauth_required") {
        setOverview((current) => (current?.available ? { ...current, freshUntil: null } : current));
      }
      setOutcome({
        tone: "error",
        text:
          cause instanceof Error ? cause.message : "Couldn't change the role. Try again shortly."
      });
      return false;
    }
  }

  function demoChange(action: "grant" | "revoke", email: string, role: string) {
    let changed = false;
    setOverview((current) => {
      if (!current?.available) return current;
      const staff = current.staff.map((member) => ({ ...member, roles: [...member.roles] }));
      let member = staff.find((item) => item.email === email.toLowerCase());
      if (!member && action === "grant") {
        member = {
          userId: crypto.randomUUID(),
          email: email.toLowerCase(),
          name: email.split("@")[0] ?? email,
          accountStatus: "active",
          twoFactorEnabled: true,
          roles: []
        };
        staff.push(member);
      }
      const held = member?.roles.some((item) => item.role === role) ?? false;
      if (member && action === "grant" && !held) {
        member.roles.push({
          role,
          assignedAt: new Date().toISOString(),
          assignedBy: session.user.email
        });
        changed = true;
      }
      if (member && action === "revoke" && held) {
        member.roles = member.roles.filter((item) => item.role !== role);
        changed = true;
      }
      return {
        ...current,
        staff: staff.filter((item) => item.roles.length),
        history: changed
          ? [
              {
                id: crypto.randomUUID(),
                action: action === "grant" ? "granted" : "revoked",
                role,
                source: "admin_console",
                actor: session.user.email,
                target: email.toLowerCase(),
                occurredAt: new Date().toISOString()
              },
              ...current.history
            ]
          : current.history
      };
    });
    return { action, email: email.toLowerCase(), role, assigned: changed, revoked: changed };
  }

  return (
    <section className="view">
      <PageHead
        title="Staff"
        count={
          loading
            ? "Loading…"
            : available
              ? `${fmtInt(available.staff.length)} with a role`
              : undefined
        }
        authority="Account platform"
      />

      {error && (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Retry", onClick: () => void load() }}
        >
          {error}
        </Notice>
      )}

      {!loading && overview && !overview.available && (
        <Empty icon={ShieldOff} title="Role changes aren't available yet">
          The account platform's release with role changes isn't live. Until it is, grant and revoke
          roles with the staff command (account-platform <code>docs/staff-roles.md</code>).
        </Empty>
      )}

      {available && (
        <>
          <FreshBanner fresh={fresh} />
          <div className="toolbar">
            <GrantForm
              roles={available.roles.filter((role) => role.consoleManaged)}
              disabled={!fresh}
              onGrant={(email, role) => change("grant", email, role)}
            />
          </div>
          {outcome && (
            <Notice
              tone={outcome.tone}
              className="toolbar-notice"
              action={{ label: "Dismiss", onClick: () => setOutcome(undefined) }}
            >
              {outcome.text}
            </Notice>
          )}
        </>
      )}

      {(loading || available) && (
        <div className="table-wrap">
          <table className="records fixed staff-table">
            <colgroup>
              {narrow ? (
                <>
                  <col style={{ width: "42%" }} />
                  <col style={{ width: "58%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "38%" }} />
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "13%" }} />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Staff member</th>
                <th scope="col">Roles</th>
                {!narrow && <th scope="col">Sign-in</th>}
                {!narrow && <th scope="col">Account</th>}
              </tr>
            </thead>
            <tbody>
              {loading || !available ? (
                <SkeletonRows columns={narrow ? 2 : 4} rows={4} />
              ) : (
                available.staff.map((member) => (
                  <StaffRow
                    key={member.userId}
                    member={member}
                    roles={available.roles}
                    you={member.userId === available.you}
                    canChange={Boolean(fresh)}
                    narrow={narrow}
                    onRevoke={(role) => change("revoke", member.email, role)}
                  />
                ))
              )}
            </tbody>
          </table>
          {available && <History overview={available} narrow={narrow} />}
        </div>
      )}
    </section>
  );
}

function FreshBanner({ fresh }: { fresh: string | null }) {
  if (fresh)
    return (
      <Notice tone="info" className="toolbar-notice" quiet>
        <b>Role changes are open until {fmtTime(fresh)}</b>, {freshSignInMinutes} minutes after you
        last signed in. Platform administrator and your own roles can't be changed here.
      </Notice>
    );
  return (
    <Notice
      tone="warn"
      className="toolbar-notice"
      quiet
      action={{
        label: "Confirm with sign-in",
        onClick: () => location.assign("/auth/login?reauth=1")
      }}
    >
      <b>Sign in again to change roles.</b> Role changes need a sign-in from the last{" "}
      {freshSignInMinutes} minutes: with your passkey, or your password (and your authenticator code
      if asked). You'll come back here.
    </Notice>
  );
}

function GrantForm({
  roles,
  disabled,
  onGrant
}: {
  roles: StaffRole[];
  disabled: boolean;
  onGrant: (email: string, role: StaffRole) => Promise<boolean>;
}) {
  // Read once per mount: a grant typed before "Confirm with sign-in" comes back with the page.
  const [saved] = useState(readPending);
  const [email, setEmail] = useState(saved.email ?? "");
  // Reviewers get Stacks operator (IN-56), so it's the default.
  const [roleKey, setRoleKey] = useState(
    roles.find((item) => item.role === saved.role)?.role ??
      roles.find((item) => item.role === "stacks_operator")?.role ??
      roles[0]?.role ??
      ""
  );
  const [busy, setBusy] = useState(false);
  const role = roles.find((item) => item.role === roleKey);

  // Keep what's typed for the round trip through "Confirm with sign-in".
  useEffect(() => {
    if (email) sessionStorage.setItem(pendingKey, JSON.stringify({ email, role: roleKey }));
    else sessionStorage.removeItem(pendingKey);
  }, [email, roleKey]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!role) return;
    setBusy(true);
    const granted = await onGrant(email.trim(), role);
    setBusy(false);
    if (granted) setEmail("");
  }

  return (
    <form className="invite-form" onSubmit={(event) => void submit(event)}>
      <input
        type="email"
        required
        aria-label="Email of the Evidite account to grant a role"
        placeholder="teammate@evidite.com"
        autoComplete="off"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <select
        aria-label="Role to grant"
        className="staff-role-select"
        value={roleKey}
        onChange={(event) => setRoleKey(event.target.value)}
      >
        {roles.map((item) => (
          <option key={item.role} value={item.role}>
            {item.displayName}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={disabled || busy || !email.trim() || !role}
      >
        <UserPlus aria-hidden="true" /> {busy ? "Granting…" : "Grant role"}
      </button>
      {role && (
        <small className="toolbar-note">
          {role.displayName}: {role.permissions.join(", ")}
        </small>
      )}
      <small className="toolbar-note">
        The person needs an Evidite account with an authenticator app, or a passkey with the
        password turned off (Security, at accounts.evidite.com).
      </small>
    </form>
  );
}

/**
 * Which staff-grade sign-in the account has (account-platform's `secureSignIn`). Before
 * account-platform's release with passkey staff sign-in, only the authenticator app counted.
 */
function SignInStatus({ member }: { member: StaffMember }) {
  const method = staffSignIn(member);
  return <StatusDot value={method ?? "password_only"} tone={method ? "ok" : "bad"} />;
}

function StaffRow({
  member,
  roles,
  you,
  canChange,
  narrow,
  onRevoke
}: {
  member: StaffMember;
  roles: StaffRole[];
  you: boolean;
  canChange: boolean;
  narrow: boolean;
  onRevoke: (role: StaffRole) => Promise<boolean>;
}) {
  const [confirming, setConfirming] = useState<string>();
  const [busy, setBusy] = useState(false);
  const roleFor = (key: string) =>
    roles.find((item) => item.role === key) ?? {
      role: key,
      displayName: key,
      permissions: [],
      consoleManaged: false
    };
  const pending = confirming ? roleFor(confirming) : undefined;

  async function revoke(role: StaffRole) {
    setBusy(true);
    await onRevoke(role);
    setBusy(false);
    setConfirming(undefined);
  }

  return (
    <tr>
      <td className="cell-primary">
        <span className="cell-stack">
          <span className="truncate">
            {member.email}
            {you && <span className="tag">You</span>}
          </span>
          <small className="truncate">{member.name}</small>
        </span>
      </td>
      <td>
        {pending ? (
          <span className="cell-actions staff-confirm">
            <span>
              Revoke <b>{pending.displayName}</b>?
            </span>
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => setConfirming(undefined)}
            >
              Keep
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              onClick={() => void revoke(pending)}
            >
              {busy ? "Revoking…" : "Revoke"}
            </button>
          </span>
        ) : (
          <span className="staff-roles">
            {member.roles.map((assignment) => {
              const role = roleFor(assignment.role);
              const revocable = role.consoleManaged && !you && canChange;
              const assigned = `Granted ${fmtRelative(assignment.assignedAt).toLowerCase()}${
                assignment.assignedBy ? ` by ${assignment.assignedBy}` : ""
              }`;
              return (
                <span key={assignment.role} className="chip" title={assigned}>
                  {role.role === "platform_admin" ? (
                    <KeyRound aria-hidden="true" />
                  ) : (
                    <ShieldCheck aria-hidden="true" />
                  )}
                  {role.displayName}
                  {revocable && (
                    <button
                      type="button"
                      className="chip-remove"
                      aria-label={`Revoke ${role.displayName} from ${member.email}`}
                      onClick={() => setConfirming(role.role)}
                    >
                      <X aria-hidden="true" />
                    </button>
                  )}
                </span>
              );
            })}
          </span>
        )}
      </td>
      {!narrow && (
        <td>
          <SignInStatus member={member} />
        </td>
      )}
      {!narrow && (
        <td>
          <StatusDot value={member.accountStatus} />
        </td>
      )}
    </tr>
  );
}

const sourceLabel: Record<string, string> = {
  admin_console: "Console",
  staff_command: "Staff command"
};

function History({ overview, narrow }: { overview: Available; narrow: boolean }) {
  const label = (key: string | null) =>
    overview.roles.find((item) => item.role === key)?.displayName ?? key ?? "Unknown role";
  return (
    <section className="staff-history" aria-labelledby="staff-history-title">
      <h2 id="staff-history-title" className="section-label">
        Recent role changes
      </h2>
      {overview.history.length ? (
        <table className="records fixed">
          <colgroup>
            {narrow ? (
              <>
                <col style={{ width: "62%" }} />
                <col style={{ width: "38%" }} />
              </>
            ) : (
              <>
                <col style={{ width: "16%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr>
              {!narrow && <th scope="col">When</th>}
              <th scope="col">Change</th>
              {!narrow && <th scope="col">Staff member</th>}
              <th scope="col">By</th>
              {!narrow && <th scope="col">From</th>}
            </tr>
          </thead>
          <tbody>
            {overview.history.map((event) => (
              <tr key={event.id}>
                {!narrow && (
                  <td className="cell-faint" title={new Date(event.occurredAt).toLocaleString()}>
                    {fmtRelative(event.occurredAt)}
                  </td>
                )}
                <td className="cell-primary">
                  <span className="cell-stack">
                    <span className="truncate">
                      {event.action === "granted" ? "Granted" : "Revoked"} {label(event.role)}
                    </span>
                    {narrow && <small className="truncate">{event.target ?? "—"}</small>}
                  </span>
                </td>
                {!narrow && <td className="cell-muted truncate">{event.target ?? "—"}</td>}
                <td className="cell-muted truncate">{event.actor ?? "—"}</td>
                {!narrow && (
                  <td className="cell-faint">{sourceLabel[event.source] ?? event.source}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No role changes recorded yet.</p>
      )}
    </section>
  );
}
